import React, { useState, useEffect } from "react";
import "../styles/Inventory.css";
import config from "../config";
import * as XLSX from "xlsx";

export default function Inventory() {
  const [inventory, setInventory] = useState([]);
  const [filteredInventory, setFilteredInventory] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [summary, setSummary] = useState({
    totalStock: 0,
    lowStockItems: 0,
    outOfStockItems: 0,
    totalInventoryValue: 0,
  });

  const [editData, setEditData] = useState(null);
  const [editId, setEditId] = useState(null);

  const loadInventory = () => {
    fetch(`${config.baseUrl}/api/inventory/dashboard`)
      .then((res) => res.json())
      .then((data) => {
        setSummary({
          totalStock: data.totalStock,
          lowStockItems: data.lowStockItems,
          outOfStockItems: data.outOfStockItems,
          totalInventoryValue: data.totalInventoryValue,
        });

        const allItems = [];
        data.vendorSummary.forEach((v) => {
          v.items.forEach((item) => {
            allItems.push({
              id: item.variantId,
              variantId: item.variantId,
              vendorName: item.vendorName,
              product: item.productName,
              sku: item.sku,
              color: item.color,
              size: item.sizeName,
              stock: item.currentStock,
              status: item.status,
              offerPrice: item.offerPrice || "-",
              originalPrice: item.originalPrice || "-",
              value: item.totalValue,
              date: item.lastUpdated || "",
            });
          });
        });

        setInventory(allItems);
        setFilteredInventory(allItems);
      })
      .catch((err) => console.error("Error loading inventory:", err));
  };

  useEffect(() => {
    loadInventory();
  }, []);

  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);

    if (term.trim() === "") {
      setFilteredInventory(inventory);
    } else {
      const filtered = inventory.filter(
        (item) =>
          item.product.toLowerCase().includes(term) ||
          item.sku.toLowerCase().includes(term) ||
          item.vendorName.toLowerCase().includes(term) ||
          item.color.toLowerCase().includes(term) ||
          item.size.toLowerCase().includes(term)
      );
      setFilteredInventory(filtered);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const d = String(date.getDate()).padStart(2, "0");
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const y = date.getFullYear();
    return `${d}/${m}/${y}`;
  };

  const formatCurrency = (num) => {
    if (!num || isNaN(num)) return "-";
    return `₹${parseFloat(num).toLocaleString("en-IN")}`;
  };

  const toggleEdit = (id) => {
    if (editId === id) {
      setEditId(null);
      setEditData(null);
    } else {
      const item = inventory.find((x) => x.id === id);
      setEditId(id);
      setEditData({ ...item });
    }
  };

  const handleSave = () => {
    const today = new Date().toISOString();
    const updated = inventory.map((item) =>
      item.id === editId ? { ...editData, date: today } : item
    );

    setInventory(updated);
    setFilteredInventory(updated);
    setEditId(null);
    setEditData(null);
  };

  const handleCancel = () => {
    setEditId(null);
    setEditData(null);
  };

  // DOWNLOAD Excel/Report
  const downloadExcel = () => {
    const exportData = filteredInventory.map((item) => ({
      "Variant ID": item.variantId,
      Vendor: item.vendorName,
      Product: item.product,
      SKU: item.sku,
      Color: item.color,
      Size: item.size,
      Stock: item.stock,
      Status: item.status,
      "Offer Price": item.offerPrice,
      "Original Price": item.originalPrice,
      Value: item.value,
      "Last Updated": formatDate(item.date),
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Inventory");

    XLSX.writeFile(workbook, "Inventory_Report.xlsx");
  };

  return (
    <div className="inv-container fade-in">
      <div className="inv-header">
        <h2>Inventory Management</h2>
        <p>Track and manage inventory levels</p>
      </div>

      <div className="inv-summary">
        <div className="inv-card blue" onClick={loadInventory}>
          <h3>{summary.totalStock}</h3>
          <p>Total Stock</p>
        </div>

        <div className="inv-card yellow" onClick={loadInventory}>
          <h3>{summary.lowStockItems}</h3>
          <p>Low Stock Items</p>
        </div>

        <div className="inv-card red" onClick={loadInventory}>
          <h3>{summary.outOfStockItems}</h3>
          <p>Out Of Stock Items</p>
        </div>

        <div className="inv-card green" onClick={loadInventory}>
          <h3>₹ {summary.totalInventoryValue}</h3>
          <p>Total Inventory Value</p>
        </div>
      </div>

      <div className="inv-vendor-title">Vendor Inventory Summary</div>

      <div className="inv-subcards">
        <span>Total Items: {inventory.length}</span>
        <span>Total Stock: {summary.totalStock}</span>
        <span>Low Stock: {summary.lowStockItems}</span>
        <span>Out Of Stock: {summary.outOfStockItems}</span>
      </div>

      {/* ✅ Search + Download in same line */}
      <div className="search-download-row">
        <div className="search-container">
          <input
            type="text"
            placeholder="🔍 Search by Product, SKU..."
            value={searchTerm}
            onChange={handleSearch}
          />
        </div>

        <button className="download-btn-small" onClick={downloadExcel}>
          ⬇ Download Report
        </button>
      </div>

      {/* TABLE */}
      <div className="inv-table-scroll">
        <table className="inv-table">
          <thead>
            <tr>
              <th>Edit</th>
              <th>Variant ID</th>
              <th>Vendor</th>
              <th>Product</th>
              <th>SKU</th>
              <th>Color</th>
              <th>Size</th>
              <th>Current Stock</th>
              <th>Status</th>
              <th>Offer Price</th>
              <th>Original Price</th>
              <th>Value</th>
              <th>Last Updated</th>
            </tr>
          </thead>

          <tbody>
            {filteredInventory.length > 0 ? (
              filteredInventory.map((item) => (
                <React.Fragment key={item.id}>
                  <tr className="row-anim">
                    <td>
                      <span className="edit-icon" onClick={() => toggleEdit(item.id)}>
                        ✏️
                      </span>
                    </td>

                    <td>{item.variantId}</td>
                    <td>{item.vendorName}</td>
                    <td className="full-text">{item.product}</td>
                    <td className="full-text sku-text">{item.sku}</td>
                    <td>{item.color}</td>
                    <td>{item.size}</td>
                    <td>{item.stock}</td>

                    <td className="nowrap-cell">
                      <span
                        className={`inv-status ${
                          item.status === "In Stock"
                            ? "green"
                            : item.status === "Low Stock"
                            ? "yellow"
                            : "red"
                        }`}
                      >
                        {item.status}
                      </span>
                    </td>

                    <td>{formatCurrency(item.offerPrice)}</td>
                    <td>{formatCurrency(item.originalPrice)}</td>
                    <td className="nowrap-cell">{formatCurrency(item.value)}</td>
                    <td className="nowrap-cell">{formatDate(item.date)}</td>
                  </tr>

                  {editId === item.id && (
                    <tr className="edit-row fade-in">
                      <td colSpan={13}>
                        <div className="edit-box">
                          <label>
                            Product:
                            <input
                              type="text"
                              value={editData.product}
                              onChange={(e) =>
                                setEditData({ ...editData, product: e.target.value })
                              }
                            />
                          </label>

                          <label>
                            Color:
                            <input
                              type="text"
                              value={editData.color}
                              onChange={(e) =>
                                setEditData({ ...editData, color: e.target.value })
                              }
                            />
                          </label>

                          <label>
                            Size:
                            <input
                              type="text"
                              value={editData.size}
                              onChange={(e) =>
                                setEditData({ ...editData, size: e.target.value })
                              }
                            />
                          </label>

                          <label>
                            Stock:
                            <input
                              type="number"
                              value={editData.stock}
                              onChange={(e) =>
                                setEditData({ ...editData, stock: e.target.value })
                              }
                            />
                          </label>

                          <div className="edit-actions">
                            <button className="btn btn-save" onClick={handleSave}>
                              Save
                            </button>
                            <button className="btn btn-cancel" onClick={handleCancel}>
                              Cancel
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            ) : (
              <tr>
                <td colSpan="13" className="no-results">
                  No results found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
